import { IPVersions } from 'app/constants'
import { VmStorageOptions } from 'account/components/userManagement/tenants/VmStorageTypePicklist'
import { customValidator } from 'core/utils/fieldValidators'
import * as IpAddress from 'ip-address'
import { ascend, prop, sortWith } from 'ramda'
import { Subnet } from '../networks/model'

export const CREATE_VM_MODAL_TITLE = 'Deploy New VM'
export const CREATE_VM_AVAILABILITY_ZONE_ERROR = 'The requested availability zone is not available'

export const placeholderCloudInitYaml = `#cloud-config
password: winterwonderland
chpasswd: { expire: False }
ssh_pwauth: True
manage_etc_hosts: true
runcmd:
- [ 'sh', '-c', 'echo "Hello World" > /tmp/helloworld.txt']
`

export type CreateVmSourceType =
  | 'image'
  | 'installFromIso'
  | 'newVolume'
  | 'existingVolume'
  | 'snapshot'
  | 'volumeSnapshot'

export interface CreateVmFormParams extends Record<string, unknown> {
  name: string
  hostname: string
  sourceType: CreateVmSourceType | string
  image: any[]
  installerIso: any[]
  driverIso: any[]
  enableDriverIso: boolean
  attachVolumes: any[]
  volume: any[]
  volumeSize: string
  volumeType: string
  volumeSnapshot: any[]
  flavor: any[]
  deleteOnTermination: boolean
  availabilityZone: string | null
  enableHotplugging: boolean
  gpuCompatible: boolean
  networks: any[]
  selectedPorts: Record<string, string>
  fixedIPs: Record<string, string>
  selectedSubnets: Record<string, Subnet>
  count: number
  sshKey: string | null
  serverGroup: any | null
  useCloudInit: boolean
  useSecurityGroups: boolean
  cloudInitYaml: string
  securityGroups: any[]
  metadata: any[]
  hotplugMemory: number | null
  hotplugVcpu: number | null
  priority: string
}

interface CreateVmSourceTypeOption {
  label: string
  value: CreateVmSourceType
}

interface FilterCreateVmNetworksArgs {
  currentTenant?: any
  isSelfServiceUser: boolean
  networks: any[]
}

interface FilterAvailableCreateVmVolumesArgs {
  availableVolumes: any[]
  selectedBootVolumes: any[]
  sourceType: string
}

interface GetFilteredCreateVmImagesArgs {
  imageSort: string
  imageType: string
  images: any[]
  sourceType: string
  tenantVmStorageType?: VmStorageOptions
}

interface GetFilteredCreateVmFlavorsArgs {
  enableHotplugging: boolean
  flavorSort: string
  flavors: any[]
  gpuCompatible: boolean
  isVolumeSourceType: boolean
}

const allVmSourceTypeOptions: CreateVmSourceTypeOption[] = [
  { label: 'Image', value: 'image' },
  { label: 'Install from ISO', value: 'installFromIso' },
  { label: 'New Volume', value: 'newVolume' },
  { label: 'Existing Volume', value: 'existingVolume' },
  { label: 'VM Snapshot', value: 'snapshot' },
  { label: 'Volume Snapshot', value: 'volumeSnapshot' },
]

const isHotpluggableFlavor = (flavor) => flavor?.vcpus === 0 && flavor?.ram === 0

// NetworkingStep FR-009: Fixed IP validation - ensures entered IP is within network subnet CIDR
export const getPrivateIpValidator = (network) => {
  return customValidator((value) => {
    if (!value) {
      return true
    }

    try {
      const subnets = network?.subnetDetails
      const pass = subnets?.some((subnet) => {
        const testCidr =
          subnet?.ip_version === IPVersions.IPv4
            ? new IpAddress.Address4(subnet?.cidr)
            : new IpAddress.Address6(subnet?.cidr)

        return subnet?.ip_version === IPVersions.IPv4
          ? new IpAddress.Address4(value).isInSubnet(testCidr)
          : new IpAddress.Address6(value).isInSubnet(testCidr)
      })

      return pass
    } catch (err) {
      return false
    }
  }, 'IP Address must be within one of the subnet CIDRs')
}

// CustomizeStep FR-001: VM count validation - ensures count is between 1 and 20
export const countValidator = customValidator((value) => {
  return value >= 1 && value <= 20
}, 'Count must be between 1 and 20')

// FlavorStep FR-002: Hotplug memory validation - ensures hotplug RAM value is greater than 0
export const hotplugMemoryValidator = customValidator((value) => {
  return value > 0
}, 'Value must be greater than 0')

// FlavorStep FR-002: Hotplug vCPU validation - ensures hotplug vCPU value is greater than 0
export const hotplugVcpuValidator = customValidator((value) => {
  return value > 0
}, 'Value must be greater than 0')

// FR-013: Guest OS Hostname validation - allows alphanumeric, hyphens, underscores; max 253 chars
export const hostnameValidator = customValidator((value) => {
  if (!value) return true // optional field
  // Must start with alphanumeric, end with alphanumeric, can contain hyphens and underscores
  return /^[a-zA-Z0-9]([a-zA-Z0-9\-_]*[a-zA-Z0-9])?$/.test(value) && value.length <= 253
}, 'Hostname must start/end with alphanumeric, contain only alphanumeric/hyphens/underscores, and be ≤ 253 chars')

export const getCreateVmStepNumber = (sourceType: string, step: number) =>
  ['image', 'snapshot'].includes(sourceType) ? step - 1 : step

export const createDefaultCreateVmParams = (
  disabledBootFromVolume: boolean,
): CreateVmFormParams => ({
  name: '',
  hostname: '',
  sourceType: disabledBootFromVolume ? 'image' : 'newVolume',
  image: [],
  installerIso: [],
  driverIso: [],
  enableDriverIso: false,
  attachVolumes: [],
  volume: [],
  volumeSize: '20',
  volumeType: '',
  volumeSnapshot: [],
  flavor: [],
  deleteOnTermination: false,
  availabilityZone: null,
  enableHotplugging: false,
  gpuCompatible: false,
  networks: [],
  selectedPorts: {},
  fixedIPs: {},
  selectedSubnets: {},
  count: 1,
  sshKey: null,
  serverGroup: null,
  useCloudInit: false,
  useSecurityGroups: false,
  cloudInitYaml: placeholderCloudInitYaml,
  securityGroups: [],
  metadata: [],
  hotplugMemory: null,
  hotplugVcpu: null,
  priority: '',
})

export const getTenantVmStorageType = (
  tenants: any[] = [],
  currentTenant?: any,
): VmStorageOptions | undefined => {
  const tenant = tenants.find((item) => item.id === currentTenant)
  return tenant?.tags?.find((tag) => tag.startsWith('vm_storage_type='))?.split('=')[1]
}

export const getCreateVmSourceTypeOptions = (
  tenantVmStorageType?: VmStorageOptions,
): CreateVmSourceTypeOption[] => {
  const ephemeralOptions = allVmSourceTypeOptions.filter((option) =>
    ['image', 'snapshot'].includes(option.value),
  )

  const volumeOptions = allVmSourceTypeOptions.filter((option) =>
    ['installFromIso', 'newVolume', 'existingVolume', 'snapshot', 'volumeSnapshot'].includes(
      option.value,
    ),
  )

  switch (tenantVmStorageType) {
    case VmStorageOptions.ephemeral:
      return ephemeralOptions
    case VmStorageOptions.volume:
      return volumeOptions
    case VmStorageOptions.ephemeral_and_volume:
    default:
      return allVmSourceTypeOptions
  }
}

export const getAllowedVmSnapshotTypes = (tenantVmStorageType?: VmStorageOptions): string[] => {
  switch (tenantVmStorageType) {
    case VmStorageOptions.ephemeral:
      return ['VM Ephemeral Disk Snapshot']
    case VmStorageOptions.volume:
      return ['VM Volume Snapshot']
    case VmStorageOptions.ephemeral_and_volume:
    default:
      return ['VM Ephemeral Disk Snapshot', 'VM Volume Snapshot']
  }
}

// FR-001: Network filtering by tenant and role
// Filters networks by tenant ownership, self-service user restrictions, and network type (managed or simple)
export const filterCreateVmNetworks = ({
  networks,
  currentTenant,
  isSelfServiceUser,
}: FilterCreateVmNetworksArgs): any[] =>
  networks.filter((network) => {
    const isManagedNetwork = network.subnets?.length > 0
    const isSimpleNetwork = network.isSimpleNetwork

    if (!isManagedNetwork && !isSimpleNetwork) {
      return false
    }

    if (network.shared) {
      return true
    }

    if (network.tenant_id !== currentTenant) {
      return false
    }

    if (isSelfServiceUser) {
      return !network?.['router:external']
    }

    return true
  })

// PCD-8480: Decide which chosen subnets still need an auto-created port.
//
// On a managed network the UI pre-creates a port on the chosen subnet so the VM
// gets an IP from that subnet. But that must only happen when the user left
// Ports = "Automatic". If they explicitly picked a specific port, or typed a
// Private IP, that choice has to win — the same way OpenStack does it: a supplied
// port uses the port's own IP, and net + fixed_ip creates a port at that exact IP.
// Auto-creating a subnet port in those cases would silently override the user's
// choice (and leak an unused port), so we exclude those networks here.
//
// Returns only the subnet selections that still need a port pre-created for them.
export const getSubnetsNeedingPorts = (
  selectedSubnets: Record<string, Subnet> = {},
  selectedPorts: Record<string, string> = {},
): Record<string, Subnet> => {
  const subnetsNeedingPorts: Record<string, Subnet> = {}

  for (const [networkId, subnet] of Object.entries(selectedSubnets)) {
    const userPickedAPort = Boolean(selectedPorts[networkId])

    // Only auto-create a port when a subnet is chosen and the user left Ports = Automatic.
    if (subnet && !userPickedAPort) {
      subnetsNeedingPorts[networkId] = subnet
    }
  }

  return subnetsNeedingPorts
}

// FR-002: Network count aggregation
// Returns counts of managed networks (with subnets) and simple networks (Layer 2) for display in mode selection cards
export const getCreateVmNetworkCounts = (networks: any[] = []) => ({
  managedNetworksCount: networks.filter(
    (network) => !network.isSimpleNetwork && network.subnets?.length > 0,
  ).length,
  simpleNetworksCount: networks.filter((network) => network.isSimpleNetwork).length,
})

// FR-003: Volume availability filtering
// Filters volumes to show only "available" status; excludes boot volumes when sourceType is existingVolume
export const filterAvailableCreateVmVolumes = ({
  availableVolumes,
  sourceType,
  selectedBootVolumes,
}: FilterAvailableCreateVmVolumesArgs): any[] =>
  availableVolumes.filter((volume) => {
    if (volume.status !== 'available') {
      return false
    }

    if (sourceType !== 'existingVolume') {
      return true
    }

    return !selectedBootVolumes.includes(volume)
  })

// FR-004: Image filtering by source type and tenant storage type
// Filters active images by source type (snapshot vs regular vs ISO) and applies tenant VM storage type restrictions (ephemeral vs volume)
export const getFilteredCreateVmImages = ({
  images,
  sourceType,
  imageType,
  tenantVmStorageType,
  imageSort,
}: GetFilteredCreateVmImagesArgs): any[] => {
  const filteredImages = images.filter((image) => {
    if (image.status !== 'active') {
      return false
    }

    const isEphemeralSnapshot = image.imageType === 'ephemeralDiskSnapshot'
    const isVolumeSnapshot = image.imageType === 'vmVolumeSnapshot'

    if (sourceType !== 'snapshot') {
      return !(isEphemeralSnapshot || isVolumeSnapshot || image.disk_format === 'iso')
    }

    if (
      imageType === 'VM Ephemeral Disk Snapshot' ||
      tenantVmStorageType === VmStorageOptions.ephemeral
    ) {
      return isEphemeralSnapshot
    }

    if (imageType === 'VM Volume Snapshot' || tenantVmStorageType === VmStorageOptions.volume) {
      return isVolumeSnapshot
    }

    return isEphemeralSnapshot || isVolumeSnapshot
  })

  const sorter = {
    name: sortWith([ascend(prop('name'))]),
    virtualSize: sortWith([ascend(prop('virtual_size'))]),
  }[imageSort]

  return imageSort ? sorter(filteredImages) : filteredImages
}

// FR-006: Minimum available network detection
// Returns managed network with smallest availablePortAndIPs count for VM batch capacity validation
export const getMinAvailableManagedNetwork = (networks: any[] = []) => {
  const managedNetworks = networks.filter((network) => !network.isSimpleNetwork)

  if (managedNetworks.length === 0) {
    return null
  }

  return managedNetworks.reduce((minNetwork, currentNetwork) =>
    currentNetwork?.availablePortAndIPs < minNetwork?.availablePortAndIPs
      ? currentNetwork
      : minNetwork,
  )
}

// FR-007: Source type classification for volume-backed VMs
// Returns true for volume-backed sources: ISO, newVolume, existingVolume, volumeSnapshot, or snapshot with vmVolumeSnapshot imageType
export const isCreateVmVolumeSourceType = (params: CreateVmFormParams): boolean =>
  ['installFromIso', 'newVolume', 'existingVolume', 'volumeSnapshot'].includes(params.sourceType) ||
  (params.sourceType === 'snapshot' && params?.image?.[0]?.imageType === 'vmVolumeSnapshot')

// FR-005: Flavor filtering by hotplugging and GPU compatibility
// Filters flavors: shows zero-disk only for hotplugging/volume sources; filters by GPU compatibility; sorts by name/vCPU/RAM/disk
export const getFilteredCreateVmFlavors = ({
  flavors,
  flavorSort,
  enableHotplugging,
  gpuCompatible,
  isVolumeSourceType,
}: GetFilteredCreateVmFlavorsArgs): any[] => {
  const filteredFlavors = flavors.filter((flavor) => {
    if (enableHotplugging !== isHotpluggableFlavor(flavor)) {
      return false
    }

    if (flavor?.gpuFlavor !== gpuCompatible) {
      return false
    }

    return isVolumeSourceType ? flavor?.disk === 0 : flavor?.disk !== 0
  })

  const sorter = {
    name: sortWith([ascend(prop('name'))]),
    vcpus: sortWith([ascend(prop('vcpus'))]),
    ram: sortWith([ascend(prop('ram'))]),
    disk: sortWith([ascend(prop('disk'))]),
  }[flavorSort]

  return flavorSort ? sorter(filteredFlavors) : filteredFlavors
}

export const shouldForceSingleVmCount = (params: CreateVmFormParams): boolean =>
  params?.sourceType === 'installFromIso' ||
  params?.sourceType === 'existingVolume' ||
  params.attachVolumes.length > 0

// FR-008: Batch creation constraint detection
// Returns { disabled, message } indicating whether batch VM creation (count > 1) is allowed based on source type, attached volumes, port selection, and feature flags
export const getDisableMultipleCreateVmsState = (
  params: CreateVmFormParams,
  enableSimpleNetworkBatchCreation: boolean,
) => {
  if (params.sourceType === 'installFromIso') {
    return {
      disabled: true,
      message: 'Bulk VM Creation is not supported when installing from ISO.',
    }
  }

  if (params.sourceType === 'existingVolume') {
    return {
      disabled: true,
      message: 'Bulk VM Creation is not supported if VM is created from existing volume  ',
    }
  }

  if (params.attachVolumes.length > 0) {
    return {
      disabled: true,
      message: 'Bulk VM Creation is not supported if additional volumes are attached to the VM',
    }
  }

  if (params?.networks?.some((network) => params?.selectedPorts?.[network.id])) {
    return {
      disabled: true,
      message: 'You can create only 1 VM when a specific port is selected from a network.',
    }
  }

  if (
    !enableSimpleNetworkBatchCreation &&
    params?.networks?.some((network) => network.isSimpleNetwork)
  ) {
    return {
      disabled: true,
      message:
        'Batch VM creation with Layer 2 Networks is not supported. Please create VMs one at a time.',
    }
  }

  return {
    disabled: false,
    message: '',
  }
}

// FR-009: Ephemeral root disk detection
// Returns true for image sources or snapshots with ephemeralDiskSnapshot imageType (VM will have ephemeral root disk)
export const isCreateVmUsingEphemeralRootDisk = (params: CreateVmFormParams): boolean =>
  params?.sourceType === 'image' ||
  (params?.sourceType === 'snapshot' && params?.image?.[0]?.imageType === 'ephemeralDiskSnapshot')

// FR-010: Windows OS detection
// Returns true if VM source has Windows OS type (detected from image metadata, volume image metadata, or volume snapshot source); determines password display behavior
export const isWindowsVmSource = (
  params: CreateVmFormParams,
  getVolumeSnapshotSourceVolume: (volumeSnapshot: any) => any,
): boolean => {
  if (['image', 'newVolume', 'snapshot'].includes(params.sourceType)) {
    return params?.image?.[0]?.os_type?.toLowerCase() === 'windows'
  }

  if (params.sourceType === 'existingVolume') {
    return params?.volume?.[0]?.volume_image_metadata?.os_type?.toLowerCase() === 'windows'
  }

  if (params.sourceType === 'volumeSnapshot') {
    const sourceVolume = getVolumeSnapshotSourceVolume(params?.volumeSnapshot?.[0])
    return sourceVolume?.volume_image_metadata?.os_type?.toLowerCase() === 'windows'
  }

  return false
}
